import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import socketSingleton from 'services/webSocketService';

import {
  getBusinessName,
  getGroupMemberId,
  getGroupPrimaryKey,
  getIsLoggedIn,
  getToken as getStoreToken,
} from 'store/session/selectors';

import {
  getIsShowingModal,
  lastActiveGroupSessionStorage,
} from 'utils/sessionStorageUtils';
import { ACCESS_TOKEN_STORAGE_KEY, getToken } from 'utils/token';

const useTokenManagementHook = () => {
  const localStorageTokenRef = useRef(getToken());
  const reduxToken = useSelector(getStoreToken);
  const userGroupId = useSelector(getGroupPrimaryKey);
  const businessName = useSelector(getBusinessName);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const groupMemberId = useSelector(getGroupMemberId);

  // handle refreshing the page when redux authorization token becomes out of sync with localstorage auth token
  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === ACCESS_TOKEN_STORAGE_KEY) {
        const localStorageToken = getToken();
        // if the previous auth token and the current auth token are valid tokens and different, it means we switched
        // user groups. all tabs should refresh
        if (
          localStorageTokenRef.current &&
          localStorageToken &&
          localStorageTokenRef.current !== localStorageToken
        ) {
          const isShowingModal = getIsShowingModal();
          if (!isShowingModal && isLoggedIn) {
            // if we were not showing the modal, but just changed groups, we need to update the session storage for the next page render
            lastActiveGroupSessionStorage.setState({
              lastActiveGroupId: userGroupId,
              lastActiveGroupBusinessName: businessName,
              lastActiveGroupMemberId: groupMemberId,
              showModal: true,
            });
          }
          window.location.reload(true);
        }
        // if the auth token becomes null, it means we've logged out
        // we should refresh all tabs aside from the tab where the logout originated
        else if (localStorageTokenRef.current && !localStorageToken) {
          // we just logged out
          // if the auth token is currently still set in this tab's redux store, we logged out in a different tab
          if (reduxToken) {
            socketSingleton.shutDown();
            window.location.reload(true);
          }
        }
        localStorageTokenRef.current = localStorageToken;
      }
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [reduxToken, businessName, userGroupId, isLoggedIn, groupMemberId]);
};

export default useTokenManagementHook;
